import { useState } from "react";
import { Palette, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface ColorPickerProps {
  currentColor: string;
  onColorChange: (color: string) => void;
  title: string;
  isForeground?: boolean;
}

const STANDARD_COLORS = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#B7B7B7",
  "#CCCCCC",
  "#D9D9D9",
  "#EFEFEF",
  "#F2F2F2",
  "#F3F3F3",
  "#FFFFFF",
  "#7F7F7F",
  "#808080",
  "#FFFF00",
  "#00FF00",
  "#00FFFF",
  "#0000FF",
  "#FF00FF",
  "#800000",
  "#008000",
  "#000080",
  "#800080",
  "#FF0000",
  "#FFA500",
  "#008080",
  "#C0C0C0",
  "#9999FF",
  "#993366",
  "#FFFFCC",
  "#CCFFFF",
  "#660066",
  "#FF8080",
  "#0066CC",
  "#CCCCFF",
  "#00CCFF",
  "#CCFFCC",
  "#FFCC00",
  "#FF99CC",
  "#CC99FF",
  "#FFCC99",
  "#3366FF",
  "#33CCCC",
  "#99CC00",
  "#FF9900",
  "#FF6600",
  "#666699",
  "#969696",
  "#003366",
  "#339966",
  "#003300",
  "#333300",
  "#993300",
  "#333399",
  "#333333",
];

export const ColorPicker = ({
  currentColor,
  onColorChange,
  title,
  isForeground = true,
}: ColorPickerProps) => {
  const [customColor, setCustomColor] = useState(currentColor);
  const [isOpen, setIsOpen] = useState(false);

  const handleColorSelect = (color: string) => {
    onColorChange(color);
    setCustomColor(color);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title={title}
            className="h-8 w-8 p-0 hover:bg-toolbar-hover hover:text-primary transition-smooth"
          >
            <Palette
              className={cn(
                "h-4 w-4",
                !isForeground && "fill-current opacity-50"
              )}
            />
            <div
              className="absolute bottom-0 right-0 w-3 h-3 rounded-full border border-white shadow-sm"
              style={{ backgroundColor: currentColor }}
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-2" align="start">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">{title}</h4>
            <div
              className="w-8 h-8 rounded border-2 border-border"
              style={{ backgroundColor: currentColor }}
            />
          </div>
          {/* Standard Colors Grid */}
          <div>
            <div className="grid grid-cols-8 gap-1">
              {STANDARD_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    "w-7 h-7 rounded border border-border hover:scale-110 transition-transform relative",
                    currentColor.toLowerCase() === color.toLowerCase() &&
                      "ring-2 ring-primary ring-offset-1"
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                >
                  {currentColor.toLowerCase() === color.toLowerCase() && (
                    <Check className="w-3 h-3 absolute inset-0 m-auto text-white drop-shadow" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Color Input */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Custom Color</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-12 h-9 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#000000"
                className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => handleColorSelect(customColor)}
                className="h-9 text-white"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
